/* Notification Center — "Warn Before" editor for one Hold Time entry (RTCFR 2026-10-15,
 * "Warn Before Unit Spinner").
 *
 * A dropdown button showing the entry's warnings ("2 Days, 4 Hours", or "—") that opens a
 * compact popup with EXACTLY TWO input controls on one line:
 *   • left  — a numeric-only textbox (a whole number greater than 0), and
 *   • right — a unit spinner that cycles Days · Hours · Minutes with ▲ / ▼ (or ↑ / ↓),
 * then "+ Add Time", which adds that value + unit as ONE independent warning to the list
 * below (2 Days, 4 Hours and 30 Minutes are three warnings, never one combined duration).
 * Each listed warning has its own Remove. Cancel discards, OK applies.
 *
 * Checked as it is added (holdTime.ts — the same rules the server enforces, in minutes): a
 * whole number > 0, strictly less than THIS entry's hold time, and not the same duration as a
 * warning already listed (1 Day = 24 Hours). Letters, decimals and minus signs never get into
 * the box. A value typed but not yet added is added by OK when valid, and blocks OK when not.
 *
 * Popup keyboard contract (CLAUDE.md standing rule 14): focus moves INTO the popup on open (the
 * value box); Enter in the value box = + Add Time; ↑/↓ on the spinner change the unit; Esc =
 * Cancel and focus returns to the trigger. Keys are stopped at the popup so they never reach
 * the grid or the wizard behind it. Rendered in a portal clamped inside the viewport. */
import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { markUserEdit } from '../../hooks/useUnsavedGuard';
import {
  cycleUnit, DEFAULT_WARN_UNIT, describeWarnings, fmtWarn, MAX_WARN_VALUES, parseWarnValue, sortWarnings, warnError, warnMinutes,
  type NcWarn, type WarnUnit,
} from './holdTime';

interface Props {
  values: NcWarn[];
  holdHours?: number | null;
  /** The hold time's label (e.g. "14 days"), used in the "less than" message. */
  holdTime?: string | null;
  /** "Test / Method · Before Prep" — names the popup for screen readers. */
  label: string;
  onChange: (values: NcWarn[]) => void;
  /** View mode: a plain read-only value, no popup. */
  readOnly?: boolean;
  disabled?: boolean;
}

/** Compact: just wide enough for the two narrow controls
 *  (RTCFR "Hold Time Target Grid UI Improvements" → 232px, then "Hold Time Alignment and Warn
 *  Before Pop-up Size" → 208px, 2026-10-15; → 182px, 2026-10-16): exactly the two controls
 *  (70px value box + 86px unit spinner) plus padding. The "Hold time" note sits on its own line
 *  above Cancel · OK, so the footer no longer sets the width; the note truncates with a tooltip
 *  if a long hold-time label ever appears. */
const POP_WIDTH = 182;

export function WarnBeforeEditor({ values, holdHours, holdTime, label, onChange, readOnly = false, disabled = false }: Props) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState<NcWarn[]>([]);
  const [text, setText] = useState('');
  const [unit, setUnit] = useState<WarnUnit>(DEFAULT_WARN_UNIT);
  const [error, setError] = useState<string | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef<HTMLInputElement>(null);

  const sortDesc = (l: NcWarn[]) => [...l].sort((a, b) => warnMinutes(b) - warnMinutes(a));

  function openEditor() {
    if (disabled) return;
    setList(sortDesc(values));
    setText('');
    setUnit(DEFAULT_WARN_UNIT);
    setError(null);
    setOpen(true);
  }
  function close(returnFocus = true) {
    setOpen(false);
    setPos(null);
    if (returnFocus) triggerRef.current?.focus();
  }

  // Position under the trigger, clamped inside the enclosing modal (the New Rule wizard) or
  // else the viewport, flipping above when there is no room below — so the popup is never cut
  // off by the modal or by the grid's own scroll box.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const t = triggerRef.current?.getBoundingClientRect();
      const h = popRef.current?.offsetHeight ?? 160;
      if (!t) return;
      const m = triggerRef.current?.closest('.nc-modal')?.getBoundingClientRect();
      const box = { left: m?.left ?? 0, top: m?.top ?? 0, right: m?.right ?? window.innerWidth, bottom: m?.bottom ?? window.innerHeight };
      // Opens from the trigger's LEFT edge (it would otherwise hang left over the Alert column),
      // shifting left only as far as needed to stay inside the modal / viewport.
      const left = Math.max(box.left + 8, Math.min(t.left, box.right - POP_WIDTH - 8));
      const below = t.bottom + 4;
      const top = below + h > box.bottom - 8 ? Math.max(box.top + 8, t.top - h - 4) : below;
      setPos({ left, top });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [open, list.length, error]);

  // Focus moves INTO the popup (the value box) on open.
  useEffect(() => {
    if (open && pos) valueRef.current?.focus();
    // Only on open — not on every reposition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pos === null]);

  // Click outside = Cancel (nothing is applied).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const n = e.target as Node;
      if (popRef.current?.contains(n) || triggerRef.current?.contains(n)) return;
      close(false);
    };
    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [open]);

  /** The value + unit in the two controls, checked against the list; null + error when invalid. */
  function pending(current: NcWarn[]): NcWarn | null {
    const value = parseWarnValue(text);
    const w: NcWarn = { value, unit };
    if (current.length >= MAX_WARN_VALUES) { setError(`At most ${MAX_WARN_VALUES} warn times.`); return null; }
    const err = warnError(w, holdHours, holdTime, current);
    setError(err);
    return err ? null : w;
  }

  function addTime() {
    const w = pending(list);
    if (!w) return;
    setList(sortDesc([...list, w]));
    setText('');
    setError(null);
    valueRef.current?.focus();
  }

  function apply() {
    let next = list;
    if (text.trim() !== '') {            // a typed value not yet added counts — when it is valid
      const w = pending(list);
      if (!w) return;
      next = sortDesc([...list, w]);
    }
    onChange(next);
    markUserEdit(triggerRef.current);
    close();
  }

  function onPopKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); }
    else if (e.key === 'Enter' && !(e.target as HTMLElement).closest('button')) {
      e.preventDefault(); e.stopPropagation();
      if ((e.target as HTMLElement) === valueRef.current && text.trim() !== '') addTime(); else apply();
    } else if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'].includes(e.key)) {
      // The popup owns its arrows — never let them move the grid row behind it.
      e.stopPropagation();
    }
  }

  function onSpinKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'ArrowUp') { e.preventDefault(); setUnit((u) => cycleUnit(u, 1)); setError(null); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setUnit((u) => cycleUnit(u, -1)); setError(null); }
  }

  if (readOnly) return <span className="nc-wb-value"><WarnChips values={values} /></span>;

  return (
    <>
      <button ref={triggerRef} type="button" className="nc-wb-trigger" disabled={disabled}
        aria-haspopup="dialog" aria-expanded={open} title={`Warn before — ${label}`}
        onClick={(e) => { e.stopPropagation(); if (open) close(false); else openEditor(); }}
        onKeyDown={(e) => { if (e.key === 'ArrowDown' && e.altKey) { e.preventDefault(); e.stopPropagation(); openEditor(); } }}>
        <WarnChips values={values} />
        <i className="ti ti-chevron-down" aria-hidden />
      </button>
      {open && createPortal(
        <div ref={popRef} className="nc-wb-pop" role="dialog" aria-modal="true" aria-label={`Warn before — ${label}`}
          style={{ left: pos?.left ?? -9999, top: pos?.top ?? -9999, width: POP_WIDTH }}
          onKeyDown={onPopKeyDown} onClick={(e) => e.stopPropagation()}>
          <div className="nc-wb-compose">
            <label className="nc-wb-field">
              <span className="nc-wb-lbl">Warning value</span>
              <input ref={valueRef} className={`nc-wb-input${error ? ' invalid' : ''}`} inputMode="numeric"
                value={text} aria-invalid={!!error} aria-label="Warning value"
                onChange={(e) => {
                  // Numeric only: anything but digits (letters, '.', '-') never gets in.
                  if (/^\d*$/.test(e.target.value)) { setText(e.target.value); setError(null); }
                  else setError('Whole numbers only.');
                }} />
            </label>
            <div className="nc-wb-field">
              <span className="nc-wb-lbl">Time unit</span>
              <div className="nc-wb-spin" role="spinbutton" tabIndex={0} aria-label="Time unit"
                aria-valuetext={unit} aria-valuenow={['Minutes', 'Hours', 'Days'].indexOf(unit)} aria-valuemin={0} aria-valuemax={2}
                onKeyDown={onSpinKeyDown}>
                <span className="nc-wb-unit">{unit}</span>
                <span className="nc-wb-spin-btns">
                  <button type="button" tabIndex={-1} aria-label="Next unit" onClick={() => { setUnit((u) => cycleUnit(u, 1)); setError(null); }}>▲</button>
                  <button type="button" tabIndex={-1} aria-label="Previous unit" onClick={() => { setUnit((u) => cycleUnit(u, -1)); setError(null); }}>▼</button>
                </span>
              </div>
            </div>
          </div>
          {error && <div className="nc-wb-err" role="alert">{error}</div>}
          <button type="button" className="nc-wb-add" onClick={addTime} disabled={list.length >= MAX_WARN_VALUES}>
            <i className="ti ti-plus" /> Add Time
          </button>
          {/* Rendered only once there is a warning, so the popup grows with its list. */}
          {list.length > 0 && <div className="nc-wb-lbl nc-wb-list-cap">{list.length} separate alert{list.length === 1 ? '' : 's'}</div>}
          {list.length > 0 && <ul className="nc-wb-list" aria-label="Warnings">
            {list.map((w) => (
              <li key={warnMinutes(w)} className="nc-wb-item">
                <span>{fmtWarn(w)}</span>
                <button type="button" className="nc-wb-remove" aria-label={`Remove ${fmtWarn(w)}`} title="Remove"
                  onClick={() => setList((cur) => cur.filter((x) => warnMinutes(x) !== warnMinutes(w)))}>
                  <i className="ti ti-trash" />
                </button>
              </li>
            ))}
          </ul>}
          <div className="nc-wb-foot">
            <span className="nc-wb-hold" title={`Hold time: ${holdTime ?? '—'}`}>Hold time: {holdTime ?? '—'}</span>
            <button type="button" className="nc-wb-btn" onClick={() => close()}>Cancel</button>
            <button type="button" className="nc-wb-btn primary" onClick={apply}>OK</button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

/** Chips shown on the trigger (and read-only): ONE chip per warning, so "2 Days" and "10 Hours"
 *  read as two separate alerts, never as one 2-days-10-hours duration (owner report
 *  2026-10-15). More than {@link CHIPS_SHOWN} collapse into "+N"; the tooltip spells it out. */
const CHIPS_SHOWN = 2;
function WarnChips({ values }: { values: NcWarn[] }) {
  if (values.length === 0) return <span className="nc-wb-text" title={describeWarnings(values)}>—</span>;
  const sorted = sortWarnings(values);
  const shown = sorted.length > CHIPS_SHOWN + 1 ? sorted.slice(0, CHIPS_SHOWN) : sorted;
  const more = sorted.length - shown.length;
  return (
    <span className="nc-wb-chips" title={describeWarnings(values)} aria-label={describeWarnings(values)}>
      {shown.map((w) => <span key={warnMinutes(w)} className="nc-wb-chip" aria-hidden="true">{fmtWarn(w)}</span>)}
      {more > 0 && <span className="nc-wb-chip nc-wb-chip--more" aria-hidden="true">+{more}</span>}
    </span>
  );
}
